/**
 * Configuration utility for dynamic API base URL determination
 */

interface ApiConfig {
  baseUrl: string;
  environment: string; // 'local' | 'plan' | 'production' | 'qa' | 'staging' | 'dev' | etc.
}

/**
 * Determines the API base URL based on the current domain
 */
export const getApiConfig = (): ApiConfig => {
  const hostname = window.location.hostname;
  const protocol = window.location.protocol;

  // Check for environment variable override
  const apiOverride = process.env.REACT_APP_API_OVERRIDE;
  if (apiOverride) {
    return {
      baseUrl: apiOverride,
      environment: hostname.includes('localhost') ? 'local' : 'production'
    };
  }

  // Local development
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return {
      baseUrl: `https://localhost:44314/api/card-view-request`,
      environment: 'local'
    };
  }

  // Production and staging environments
  if (hostname.includes('pcibooking.net')) {
    // Extract stage suffix if present
    // static.pcibooking.net -> service.pcibooking.net
    // static-qa.pcibooking.net -> service-qa.pcibooking.net
    // static-staging.pcibooking.net -> service-staging.pcibooking.net
    // static-dev.pcibooking.net -> service-dev.pcibooking.net
    
    const match = hostname.match(/^static(-\w+)?\.pcibooking\.net$/);
    const stageSuffix = match && match[1] ? match[1] : '';
    const apiDomain = `service${stageSuffix}.pcibooking.net`;
    
    return {
      baseUrl: `${protocol}//${apiDomain}/api/card-view-request`,
      environment: stageSuffix ? stageSuffix.substring(1) : 'production' // Remove the '-' prefix
    };
  }

  // Fallback to localhost for unknown domains
  return {
    baseUrl: `https://localhost:44314/api/card-view-request`,
    environment: 'local'
  };
};

/**
 * Get the full API endpoint URL
 */
export const getApiUrl = (endpoint: string): string => {
  const config = getApiConfig();
  return `${config.baseUrl}${endpoint}`;
};

/**
 * Get current environment
 */
export const getEnvironment = (): string => {
  return getApiConfig().environment;
};

/**
 * Check if running in local development
 */
export const isLocalDevelopment = (): boolean => {
  return getApiConfig().environment === 'local';
};

/**
 * Detect if the user is on a mobile device
 */
export const isMobileDevice = (): boolean => {
  // Check user agent for mobile indicators
  const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;

  // Check for mobile patterns in user agent
  const mobileRegex = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;
  const isMobileUA = mobileRegex.test(userAgent);

  // Check for touch support and small screen
  const hasTouchScreen = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  const isSmallScreen = window.innerWidth <= 768;

  // Consider it mobile if it matches mobile user agent OR (has touch AND small screen)
  return isMobileUA || (hasTouchScreen && isSmallScreen);
};